/* ============================================================
   LOOKBOOK CAROUSEL — section-lookbook-carousel.js
   Implements the expo display carousel as a Web Component.
   Scoped to .lkbk- prefixed elements; no global side-effects.
   ============================================================ */

(function () {
  'use strict';

  /* ============================================================
     SLIDE DATA
     45 slides: 1 hook + 5 chapter cards + 38 product slides + 1 CTA.
     Image URLs pulled verbatim from .tmp/expo-display/products.json.

     Chapter → products.json theme mapping:
       couple   ← "couple" + "signage" themes (14 slides)
       party    ← "party" theme (7 slides)
       sip      ← "bachelorette" theme (7 slides)
       favors   ← "favors" theme, incl. dual-theme favors+couple (8 slides)
       fur      ← "familiars" theme (2 slides; only 2 familiars images exist —
                  padding with party/favors images was considered but the 2
                  familiars shots are strong enough to stand alone)

     NOTE: body fields contain <em> tags from products.json blurb.
     _buildContent renders product body via innerHTML (see below).
  ============================================================ */

  const CHAPTERS = {
    couple: 'For the Couple',
    party:  'For Your Party',
    sip:    'Sip in Style',
    favors: 'Favors They\'ll Keep',
    fur:    'Your Fur Baby Too',
    cta:    ''
  };

  const SLIDES = [
    // ── SLIDE 0: HOOK ──────────────────────────────────────────
    {
      type:       'hook',
      chapter:    '',
      bg:         'https://cdn.shopify.com/s/files/1/0984/3464/6308/files/WeddingExpo-WeddingSign.jpg?v=1777825934',
      bgPosition: 'center 25%',
      eyebrow:    'Queer AF Wedding Expo',
      heading:    'These could be <em>on your wedding day.</em>',
      body:       'Custom, handmade, and made for couples who celebrate every beautiful, strange, wonderful thing about being them.',
      product:    ''
    },

    // ── SLIDE 1: Chapter — For the Couple ──────────────────────
    {
      type:       'chapter',
      chapter:    'couple',
      bg:         'https://cdn.shopify.com/s/files/1/0984/3464/6308/files/WeddingExpo-SashBride.png?v=1777825928',
      bgPosition: 'center center',
      chapterNum: 'Chapter One',
      heading:    'For the <em>Couple</em>',
      tagline:    'Walk down the aisle wearing your love.'
    },

    // ── SLIDES 2–15: For the Couple (14 product slides) ────────

    // 2: Welcome Sign — most striking opener; sets the scene
    {
      type:       'product',
      chapter:    'couple',
      bg:         'https://cdn.shopify.com/s/files/1/0984/3464/6308/files/WeddingExpo-WeddingSign.jpg?v=1777825934',
      bgPosition: 'center 25%',
      eyebrow:    'For the Couple',
      heading:    'Set the tone <em>the moment they arrive.</em>',
      body:       'Set the tone the moment they arrive. <em>Custom acrylic signage</em> that says "this isn\'t your average wedding."',
      product:    'Welcome Sign'
    },
    // 3: Welcome Sign 2 — second striking signage shot
    {
      type:       'product',
      chapter:    'couple',
      bg:         'https://cdn.shopify.com/s/files/1/0984/3464/6308/files/WeddingExpo-WeddingSign2.jpg?v=1777825935',
      bgPosition: 'center 25%',
      eyebrow:    'For the Couple',
      heading:    'Bold names, <em>bold day.</em>',
      body:       'Bold names, bold day. Acrylic welcome signs <em>conjured to your colors</em> — match your bouquet, your bridesmaids, your vibe.',
      product:    'Welcome Sign · Custom Colors'
    },
    // 4: Reception Welcome Sign — interior welcome sign
    {
      type:       'product',
      chapter:    'couple',
      bg:         'https://cdn.shopify.com/s/files/1/0984/3464/6308/files/WeddingExpo-WelcomeSign.jpg?v=1777825935',
      bgPosition: 'center center',
      eyebrow:    'For the Couple',
      heading:    'First thing they see. <em>Make it gorgeous.</em>',
      body:       'First thing your guests see. <em>Make it gorgeous.</em>',
      product:    'Reception Welcome Sign'
    },
    // 5: Acrylic Bar Menu — visually striking signage interstitial
    {
      type:       'product',
      chapter:    'couple',
      bg:         'https://cdn.shopify.com/s/files/1/0984/3464/6308/files/WeddingExpo-BarMenuAcrylic.webp?v=1777825920',
      bgPosition: 'center center',
      eyebrow:    'For the Couple',
      heading:    'Signature cocktails. <em>Cinematic bar menu.</em>',
      body:       'Signature cocktails on <em>laser-cut acrylic</em> behind the bar. Frame it after.',
      product:    'Acrylic Bar Menu'
    },
    // 6: Wall Banner Mrs & Mrs — grand statement piece
    {
      type:       'product',
      chapter:    'couple',
      bg:         'https://cdn.shopify.com/s/files/1/0984/3464/6308/files/WeddingExpo-Wall-BannerMrsMrs.png?v=1777825926',
      bgPosition: 'center center',
      eyebrow:    'For the Couple',
      heading:    'The photo-wall <em>centerpiece.</em>',
      body:       'The photo-wall centerpiece. <em>Custom acrylic banners</em> hung behind the head table.',
      product:    'Wall Banner · Mrs & Mrs'
    },
    // 7: Wall Banner Custom Logo
    {
      type:       'product',
      chapter:    'couple',
      bg:         'https://cdn.shopify.com/s/files/1/0984/3464/6308/files/WeddingExpo-Wall-BannerWeddingLogo.png?v=1777825926',
      bgPosition: 'center center',
      eyebrow:    'For the Couple',
      heading:    'Your logo, <em>built into the decor.</em>',
      body:       'Your wedding logo, built into the decor. <em>Designed with you, made by us.</em>',
      product:    'Wall Banner · Custom Logo'
    },
    // 8: Wedding Invitation
    {
      type:       'product',
      chapter:    'couple',
      bg:         'https://cdn.shopify.com/s/files/1/0984/3464/6308/files/WeddingExpo-WeddingInvite.png?v=1777825923',
      bgPosition: 'center center',
      eyebrow:    'For the Couple',
      heading:    'Set the tone <em>before they RSVP.</em>',
      body:       'Set the tone before they RSVP. <em>Hand-designed invites</em> in your colors and voice.',
      product:    'Custom Wedding Invitation'
    },
    // 9: Bride Sash — wearable, personal
    {
      type:       'product',
      chapter:    'couple',
      bg:         'https://cdn.shopify.com/s/files/1/0984/3464/6308/files/WeddingExpo-SashBride.png?v=1777825928',
      bgPosition: 'center center',
      eyebrow:    'For the Couple',
      heading:    'Wear the title. <em>Make it yours.</em>',
      body:       'Wear the title. <em>Custom satin sashes</em> with your photo, your text, your color.',
      product:    'Bride Sash · Custom'
    },
    // 10: Pride Flag — queer joy centerpiece
    {
      type:       'product',
      chapter:    'couple',
      bg:         'https://cdn.shopify.com/s/files/1/0984/3464/6308/files/WeddingExpo-FlagPrideCouple.png?v=1777825927',
      bgPosition: 'center 40%',
      eyebrow:    'For the Couple',
      heading:    '<em>Queer joy</em> on the dance floor.',
      body:       '<em>Queer joy on the dance floor.</em> Custom pride flags for your first dance, your photo wall, your aisle.',
      product:    'Pride Flag · For the Couple'
    },
    // 11: Cake Topper Initials
    {
      type:       'product',
      chapter:    'couple',
      bg:         'https://cdn.shopify.com/s/files/1/0984/3464/6308/files/WeddingExpo-CakeTopperInitials.png?v=1777825934',
      bgPosition: 'center 30%',
      eyebrow:    'For the Couple',
      heading:    'Two letters. <em>One forever.</em>',
      body:       'Two letters. One forever. <em>Laser-cut acrylic cake toppers</em> for the couple writing their own rules.',
      product:    'Cake Topper · Initials'
    },
    // 12: Cake Topper Mrs & Mrs
    {
      type:       'product',
      chapter:    'couple',
      bg:         'https://cdn.shopify.com/s/files/1/0984/3464/6308/files/WeddingExpo-CakeTopperMrsMrs.png?v=1777825934',
      bgPosition: 'center 35%',
      eyebrow:    'For the Couple',
      heading:    '<em>Mrs & Mrs.</em> Mr & Mr. Whatever fits.',
      body:       '<em>Mrs & Mrs.</em> Mr & Mr. Mx & Mx. Whatever fits — we\'ll cut it for you.',
      product:    'Cake Topper · Mrs & Mrs'
    },
    // 13: Custom Jacket Pin — wearable detail
    {
      type:       'product',
      chapter:    'couple',
      bg:         'https://cdn.shopify.com/s/files/1/0984/3464/6308/files/WeddingExpo-JacketPinCouple.png?v=1777825931',
      bgPosition: 'center center',
      eyebrow:    'For the Couple',
      heading:    'Wear your love. <em>Custom pins.</em>',
      body:       'Wear your love. <em>Custom pins</em> for the leather-jacket bride and the goth groomsman.',
      product:    'Custom Couple Jacket Pin'
    },
    // 14: Tie Clip — detail shot
    {
      type:       'product',
      chapter:    'couple',
      bg:         'https://cdn.shopify.com/s/files/1/0984/3464/6308/files/WeddingExpo-TieClip.png?v=1777825924',
      bgPosition: 'center center',
      eyebrow:    'For the Couple',
      heading:    'A small, sharp <em>keepsake.</em>',
      body:       'A small, sharp keepsake. <em>Custom tie clips</em> for the wedding party — or the couple who matches.',
      product:    'Custom Tie Clip'
    },
    // 15: Tie Clip Detail — close-up, chapter closer
    {
      type:       'product',
      chapter:    'couple',
      bg:         'https://cdn.shopify.com/s/files/1/0984/3464/6308/files/WeddingExpo-TieClip2.png?v=1777825932',
      bgPosition: 'center center',
      eyebrow:    'For the Couple',
      heading:    'Print a date. <em>Made just for you.</em>',
      body:       'Print a date, an inside joke, initials, a coordinate. <em>Made just for you.</em>',
      product:    'Tie Clip · Detail'
    },
    // 16: Pride Rectangle Earrings — visible queer pride for the couple
    {
      type:       'product',
      chapter:    'couple',
      bg:         'https://cdn.shopify.com/s/files/1/0984/3464/6308/files/WeddingExpo-EarringRectangle-Pride.png?v=1777825929',
      bgPosition: 'center center',
      eyebrow:    'For the Couple',
      heading:    'The whole spectrum. <em>On your ears.</em>',
      body:       'Pride flag rectangle earrings — laser-cut acrylic, your flag, your day. <em>Subtle but unmissable.</em>',
      product:    'Pride Rectangle Earrings'
    },

    // ── SLIDE 17: Chapter — For Your Party ─────────────────────
    {
      type:       'chapter',
      chapter:    'party',
      bg:         'https://cdn.shopify.com/s/files/1/0984/3464/6308/files/WeddingExpo-BouquetCharm3Charm.png?v=1777825926',
      bgPosition: 'center center',
      chapterNum: 'Chapter Two',
      heading:    'For Your <em>Party</em>',
      tagline:    'From bachelorette to "I do" — your whole crew, covered.'
    },

    // ── SLIDES 17–23: For Your Party (7 product slides) ────────

    // 17: Custom Name Tags — opening hero shot
    {
      type:       'product',
      chapter:    'party',
      bg:         'https://cdn.shopify.com/s/files/1/0984/3464/6308/files/WeddingExpo-Nametags2.png?v=1777825933',
      bgPosition: 'center center',
      eyebrow:    'For Your Party',
      heading:    '<em>Conversation starter.</em>',
      body:       '<em>Conversation starter</em>. Pretty acrylic name tags — beats a sticker every time.',
      product:    'Custom Name Tags'
    },
    // 18: Bouquet Charms
    {
      type:       'product',
      chapter:    'party',
      bg:         'https://cdn.shopify.com/s/files/1/0984/3464/6308/files/WeddingExpo-BouquetCharm3Charm.png?v=1777825926',
      bgPosition: 'center 30%',
      eyebrow:    'For Your Party',
      heading:    'Carry your loved ones <em>down the aisle.</em>',
      body:       'Carry your loved ones down the aisle. <em>Bouquet photo charms</em> — hidden tributes only you\'ll know about.',
      product:    'Bouquet Charms (Set of 3)'
    },
    // 20: Name Placeholder Example — styled scene
    {
      type:       'product',
      chapter:    'party',
      bg:         'https://cdn.shopify.com/s/files/1/0984/3464/6308/files/WeddingExpo-NamePlaceholderExample.jpg?v=1777825922',
      bgPosition: 'center center',
      eyebrow:    'For Your Party',
      heading:    'Place settings <em>as keepsakes.</em>',
      body:       'Names laser-engraved into wood, acrylic, or metal. <em>Place settings as keepsakes.</em>',
      product:    'Custom Name Placeholder'
    },
    // 21: Name Placeholder Style 2
    {
      type:       'product',
      chapter:    'party',
      bg:         'https://cdn.shopify.com/s/files/1/0984/3464/6308/files/WeddingExpo-NamePlaceholderExample2.jpg?v=1777825921',
      bgPosition: 'center center',
      eyebrow:    'For Your Party',
      heading:    '<em>Modern, dark, beautiful.</em>',
      body:       '<em>Modern, dark, beautiful.</em>',
      product:    'Name Placeholders · Style 2'
    },
    // 22: Name Place Card Detail
    {
      type:       'product',
      chapter:    'party',
      bg:         'https://cdn.shopify.com/s/files/1/0984/3464/6308/files/WeddingExpo-NamePlaceholder.avif?v=1777825923',
      bgPosition: 'center 20%',
      eyebrow:    'For Your Party',
      heading:    'Place cards <em>nobody throws away.</em>',
      body:       '<em>Hand-engraved place cards</em> nobody throws away.',
      product:    'Name Place Card · Detail'
    },
    // 23: Napkin Ring Name Holder styled
    {
      type:       'product',
      chapter:    'party',
      bg:         'https://cdn.shopify.com/s/files/1/0984/3464/6308/files/WeddingExpo-NameHolderNapkinRing2.webp?v=1777825920',
      bgPosition: 'center center',
      eyebrow:    'For Your Party',
      heading:    'Detail <em>layered on detail.</em>',
      body:       'Name cards held in <em>laser-engraved napkin rings</em>. Detail layered on detail.',
      product:    'Napkin Ring Name Holder'
    },
    // 24: Napkin Ring detail close-up — chapter closer
    {
      type:       'product',
      chapter:    'party',
      bg:         'https://cdn.shopify.com/s/files/1/0984/3464/6308/files/WeddingExpo-NameHolderNapkinRing.jpg?v=1777825921',
      bgPosition: 'center center',
      eyebrow:    'For Your Party',
      heading:    'Take it home as a <em>keepsake.</em>',
      body:       '<em>Take it home as a keepsake</em> — a napkin ring engraved with your seat.',
      product:    'Napkin Ring · Detail'
    },
    // 25: Pronouns Earrings — statement gifts for the wedding party
    {
      type:       'product',
      chapter:    'party',
      bg:         'https://cdn.shopify.com/s/files/1/0984/3464/6308/files/WeddingExpo-Earrings-Pronouns.jpg?v=1777825933',
      bgPosition: 'center center',
      eyebrow:    'For Your Party',
      heading:    'Pronouns on display. <em>Loud and proud.</em>',
      body:       'Statement earrings for your <em>chosen family</em> — pronouns, names, whatever they want the world to know.',
      product:    'Pronouns Earrings · Custom'
    },
    // 26: Pronouns Earrings · Detail
    {
      type:       'product',
      chapter:    'party',
      bg:         'https://cdn.shopify.com/s/files/1/0984/3464/6308/files/WeddingExpo-Earrings-Pronouns2.png?v=1777825932',
      bgPosition: 'center center',
      eyebrow:    'For Your Party',
      heading:    'Made for the <em>visible queer joy.</em>',
      body:       'Acrylic pronouns earrings, laser-cut to your color palette. <em>Wear them. Gift them. Mean them.</em>',
      product:    'Pronouns Earrings · Detail'
    },

    // ── SLIDE 27: Chapter — Sip in Style ───────────────────────
    {
      type:       'chapter',
      chapter:    'sip',
      bg:         'https://cdn.shopify.com/s/files/1/0984/3464/6308/files/WeddingExpo-Sash_Koozie_Bach.jpg?v=1777825929',
      bgPosition: 'center center',
      chapterNum: 'Chapter Three',
      heading:    'Sip in <em>Style</em>',
      tagline:    'Drinkware that matches your dark-romantic energy.'
    },

    // ── SLIDES 26–32: Sip in Style (7 product slides) ──────────

    // 26: Sash + Koozie Combo — opening hero, group energy
    {
      type:       'product',
      chapter:    'sip',
      bg:         'https://cdn.shopify.com/s/files/1/0984/3464/6308/files/WeddingExpo-Sash_Koozie_Bach.jpg?v=1777825929',
      bgPosition: 'center center',
      eyebrow:    'Sip in Style',
      heading:    '<em>Bride tribe set.</em>',
      body:       '<em>Bride tribe set</em> — sashes for the squad, koozies for the cooler. Done.',
      product:    'Sash + Koozie Combo'
    },
    // 27: Bach Party Apparel
    {
      type:       'product',
      chapter:    'sip',
      bg:         'https://cdn.shopify.com/s/files/1/0984/3464/6308/files/WeddingExpo-GuyShirtBach.png?v=1777825931',
      bgPosition: 'center center',
      eyebrow:    'Sip in Style',
      heading:    'Custom shirts that <em>aren\'t matching pastel cringe.</em>',
      body:       'Custom shirts that aren\'t <em>matching pastel cringe</em>. Goth, glittery, queer, weird — your call.',
      product:    'Bach Party Apparel'
    },
    // 28: Bachelorette Koozies
    {
      type:       'product',
      chapter:    'sip',
      bg:         'https://cdn.shopify.com/s/files/1/0984/3464/6308/files/WeddingExpo-KoozieBach.png?v=1777825931',
      bgPosition: 'center center',
      eyebrow:    'Sip in Style',
      heading:    'Cold drinks, <em>hot night.</em>',
      body:       'Cold drinks, hot night. <em>Personalized koozies</em> that survive the chaos.',
      product:    'Bachelorette Koozies'
    },
    // 29: Koozies Custom Design
    {
      type:       'product',
      chapter:    'sip',
      bg:         'https://cdn.shopify.com/s/files/1/0984/3464/6308/files/WeddingExpo-KoozieBach2.png?v=1777825931',
      bgPosition: 'center center',
      eyebrow:    'Sip in Style',
      heading:    'Bring photos, lyrics, slogans. <em>We print whatever you bring.</em>',
      body:       'Bring photos, lyrics, slogans. <em>We print whatever you bring.</em>',
      product:    'Koozies · Custom Design'
    },
    // 30: Drink Pouches Bach (lifestyle shot)
    {
      type:       'product',
      chapter:    'sip',
      bg:         'https://cdn.shopify.com/s/files/1/0984/3464/6308/files/WeddingExpo-DrinkPouches_2b0e8e9e-d443-4152-9b28-bd8570db5d23.jpg?v=1777825929',
      bgPosition: 'center center',
      eyebrow:    'Sip in Style',
      heading:    'Aesthetic >>> <em>red Solo cups.</em>',
      body:       'Aesthetic >>> red Solo cups. <em>Custom drink pouches</em> for the bach trip.',
      product:    'Drink Pouches · Bach'
    },
    // 31: Drink Pouches Detail
    {
      type:       'product',
      chapter:    'sip',
      bg:         'https://cdn.shopify.com/s/files/1/0984/3464/6308/files/WeddingExpo-DrinkPouches.jpg?v=1777825928',
      bgPosition: 'center center',
      eyebrow:    'Sip in Style',
      heading:    'Reusable. Personalized. <em>Iconic group photo material.</em>',
      body:       'Reusable. Personalized. <em>Iconic group photo material.</em>',
      product:    'Drink Pouches · Detail'
    },
    // 32: Custom Drink Stirrers — chapter closer, tiny detail
    {
      type:       'product',
      chapter:    'sip',
      bg:         'https://cdn.shopify.com/s/files/1/0984/3464/6308/files/WeddingExpo-DrinkStirrersExamples.png?v=1777825926',
      bgPosition: 'center center',
      eyebrow:    'Sip in Style',
      heading:    'Tiny details that make a cocktail feel <em>cinematic.</em>',
      body:       'Tiny details that make a signature cocktail feel <em>cinematic</em>.',
      product:    'Custom Drink Stirrers'
    },

    // ── SLIDE 33: Chapter — Favors ─────────────────────────────
    {
      type:       'chapter',
      chapter:    'favors',
      bg:         'https://cdn.shopify.com/s/files/1/0984/3464/6308/files/WeddingExpo-MatchboxFavors.png?v=1777825923',
      bgPosition: 'center center',
      chapterNum: 'Chapter Four',
      heading:    'Favors They\'ll <em>Actually Keep</em>',
      tagline:    'Skip the tulle bags of almonds. Send them home with something strange and wonderful.'
    },

    // ── SLIDES 34–41: Favors They'll Keep (8 product slides) ───

    // 34: Bottle Opener — classic, useful
    {
      type:       'product',
      chapter:    'favors',
      bg:         'https://cdn.shopify.com/s/files/1/0984/3464/6308/files/WeddingExpo-BottleOpenerWeddingFavor.png?v=1777825930',
      bgPosition: 'center center',
      eyebrow:    'Favors They\'ll Keep',
      heading:    'Favors guests <em>actually use.</em>',
      body:       'Wedding favors guests <em>actually use</em>. Engraved bottle openers with your date and names.',
      product:    'Bottle Opener Favor'
    },
    // 35: Wedding Favor Pins
    {
      type:       'product',
      chapter:    'favors',
      bg:         'https://cdn.shopify.com/s/files/1/0984/3464/6308/files/WeddingExpo-PinWeddingFavor.png?v=1777825927',
      bgPosition: 'center 30%',
      eyebrow:    'Favors They\'ll Keep',
      heading:    'Tiny, pretty, <em>keepable.</em>',
      body:       'Tiny, pretty, keepable. <em>Custom enamel favors</em> guests will pin to jackets for years.',
      product:    'Wedding Favor Pins'
    },
    // 36: Matchbox Favors
    {
      type:       'product',
      chapter:    'favors',
      bg:         'https://cdn.shopify.com/s/files/1/0984/3464/6308/files/WeddingExpo-MatchboxFavors.png?v=1777825923',
      bgPosition: 'center center',
      eyebrow:    'Favors They\'ll Keep',
      heading:    'Strike a match. <em>Light a candle.</em>',
      body:       'Strike a match. Light a candle. <em>Custom matchbox favors</em> guests pocket and remember.',
      product:    'Matchbox Favors'
    },
    // 37: Keychain Favor Detail
    {
      type:       'product',
      chapter:    'favors',
      bg:         'https://cdn.shopify.com/s/files/1/0984/3464/6308/files/WeddingExpo-KeychainFavorExample2.png?v=1777825922',
      bgPosition: 'center center',
      eyebrow:    'Favors They\'ll Keep',
      heading:    'Engraved acrylic, leather, or wood. <em>Pick your finish.</em>',
      body:       'Engraved acrylic, leather, or wood. <em>Pick your finish.</em>',
      product:    'Keychain Favor · Detail'
    },
    // 38: Keychain Place Card / Favor crossover
    {
      type:       'product',
      chapter:    'favors',
      bg:         'https://cdn.shopify.com/s/files/1/0984/3464/6308/files/WeddingExpo-KeychainFavorPlacesettings.png?v=1777825923',
      bgPosition: 'center center',
      eyebrow:    'Favors They\'ll Keep',
      heading:    'Two purposes, <em>one beautiful detail.</em>',
      body:       'Two purposes, one beautiful detail. <em>Keychain place cards</em> guests take home.',
      product:    'Place Settings + Favors · Keychains'
    },
    // 39: Place Card → Keychain detail
    {
      type:       'product',
      chapter:    'favors',
      bg:         'https://cdn.shopify.com/s/files/1/0984/3464/6308/files/WeddingExpo-NamePlaceholderKeychainExample.webp?v=1777825922',
      bgPosition: 'center center',
      eyebrow:    'Favors They\'ll Keep',
      heading:    'Doubles as a favor. <em>Used forever.</em>',
      body:       'Doubles as a favor — <em>keychain place cards</em> guests use forever.',
      product:    'Place Card → Keychain'
    },
    // 40: Glass Sublimation Gift — photos baked into glass
    {
      type:       'product',
      chapter:    'favors',
      bg:         'https://cdn.shopify.com/s/files/1/0984/3464/6308/files/WeddingExpo-GlassSublimationGift.webp?v=1777825921',
      bgPosition: 'center center',
      eyebrow:    'Favors They\'ll Keep',
      heading:    'Photos baked into glass <em>forever.</em>',
      body:       'Photos baked into glass forever. <em>Gifts that don\'t fade.</em>',
      product:    'Glass Gift · Sublimation'
    },
    // 41: Glass Etching — chapter closer, premium detail
    {
      type:       'product',
      chapter:    'favors',
      bg:         'https://cdn.shopify.com/s/files/1/0984/3464/6308/files/WeddingExpo-Glass_Etching_Example.webp?v=1777825920',
      bgPosition: 'center center',
      eyebrow:    'Favors They\'ll Keep',
      heading:    'Etched glassware for <em>the toast and the keepsake.</em>',
      body:       'Etched glassware for the toast and the keepsake. <em>One-of-one.</em>',
      product:    'Glass Etching · Custom'
    },

    // ── SLIDE 42: Chapter — Your Fur Baby Too ──────────────────
    {
      type:       'chapter',
      chapter:    'fur',
      bg:         'https://cdn.shopify.com/s/files/1/0984/3464/6308/files/WeddingExpo-KoozieWeddingDog.png?v=1777825924',
      bgPosition: 'center center',
      chapterNum: 'Chapter Five',
      heading:    'Your Fur Baby <em>Too</em>',
      tagline:    'Because the best wedding guests have four legs.'
    },

    // ── SLIDES 43–44: Your Fur Baby Too (2 product slides) ─────
    // NOTE: Only 2 "familiars" images exist in products.json.
    // Padding with party/favors images was considered but these two
    // shots are strong enough to close the chapter without filler.

    // 43: Wedding Koozie Dog Edition
    {
      type:       'product',
      chapter:    'fur',
      bg:         'https://cdn.shopify.com/s/files/1/0984/3464/6308/files/WeddingExpo-KoozieWeddingDog.png?v=1777825924',
      bgPosition: 'center center',
      eyebrow:    'Your Fur Baby Too',
      heading:    'Because your dog <em>is in the wedding party.</em>',
      body:       'Because your dog is in the wedding party. <em>Obviously.</em>',
      product:    'Wedding Koozie · Dog Edition'
    },
    // 44: Koozie Favor 2 Dogs — chapter closer
    {
      type:       'product',
      chapter:    'fur',
      bg:         'https://cdn.shopify.com/s/files/1/0984/3464/6308/files/WeddingExpo-KoozieFavor-2Dogs.png?v=1777825930',
      bgPosition: 'center center',
      eyebrow:    'Your Fur Baby Too',
      heading:    'Your pups deserve <em>a starring role.</em>',
      body:       'Your pups deserve a starring role. <em>Custom koozies</em> with their faces — the favor everyone wants.',
      product:    'Koozie Favor · Featuring Pets'
    },
    // 45: Cufflinks · Dogs — formalwear with your fur babies
    {
      type:       'product',
      chapter:    'fur',
      bg:         'https://cdn.shopify.com/s/files/1/0984/3464/6308/files/WeddingExpo-CufflinksDogsExample.png?v=1777825600',
      bgPosition: 'center center',
      eyebrow:    'Your Fur Baby Too',
      heading:    'Wear your dogs <em>down the aisle.</em>',
      body:       'Custom cufflinks with your pups\' faces. <em>Quietly devastating</em> in the best way.',
      product:    'Cufflinks · Custom Pet Photo'
    },

    // ── SLIDE 46: CTA ───────────────────────────────────────────
    {
      type:       'cta',
      chapter:    'cta',
      bg:         'https://cdn.shopify.com/s/files/1/0984/3464/6308/files/WeddingExpo-WelcomeSign.jpg?v=1777825935',
      bgPosition: 'center center',
      heading:    'Ready to <em>join the coven?</em>',
      body:       'Scan to shop, or leave your email and we\'ll send you our full wedding lookbook — plus a discount just for being here today.',
      product:    ''
    }
  ];

  /* ============================================================
     WEB COMPONENT
  ============================================================ */
  class LookbookCarousel extends HTMLElement {
    constructor () {
      super();
      this._currentIndex = 0;
      this._totalSlides  = SLIDES.length;
      this._autoTimer    = null;
      this._isPaused     = false;
      this._touchStartX  = 0;
      this._touchStartY  = 0;
      this._dragOffset   = 0;
      this._isDragging   = false;
      this._mouseDown    = false;
      this._mouseStartX  = 0;
      this._idleTimer    = null;
      this._helpVisible  = false;
      this._helpDismiss  = null;
      this._holdTimer    = null;
      this._bgEls        = [];
      /* Only slides within this many positions of the current one keep a
         loaded background image; the rest are cleared so the browser can
         reclaim the decoded bitmaps (mobile memory cap). */
      this._bgWindowRadius = 2;
    }

    connectedCallback () {
      /* ── Mode detection ──────────────────────────────────────── */
      const params = new URLSearchParams(window.location.search);
      this._isExpoMode = params.get('display') === 'expo';

      /* Find scoped DOM elements within this component */
      this._root     = this;
      this._carousel = this.querySelector('.lkbk-carousel');
      this._strip    = this.querySelector('.lkbk-progress-strip');
      this._chapter  = this.querySelector('.lkbk-chapter-label');
      this._counter  = this.querySelector('.lkbk-slide-counter');
      this._navPrev  = this.querySelector('.lkbk-nav-prev');
      this._navNext  = this.querySelector('.lkbk-nav-next');
      this._loading  = this.querySelector('.lkbk-loading');
      this._inner    = this.querySelector('.lkbk-inner');
      this._helpBtn  = this.querySelector('.lkbk-help-btn');
      this._helpTip  = this.querySelector('.lkbk-help-tooltip');
      this._restart  = this.querySelector('.lkbk-restart-btn');

      /* Apply initial mode class */
      if (this._isExpoMode) {
        this._applyFullscreenMode();
      } else {
        this.classList.add('is-embedded');
        /* Mark hosting section for CSS scoping */
        const section = this.closest('.shopify-section');
        if (section) section.classList.add('lkbk-hosting-section');
      }

      /* Set CSS duration variable */
      this.style.setProperty('--lkbk-duration-css', '6s');

      this._buildSlides();
      this._applyBgWindow(0);
      this._buildProgressBars();
      this._buildFullscreenButton();
      this._buildCloseButton();
      this._updateProgressBars(0);
      this._updateChapterLabel();
      this._updateCounter();
      this._updateRootClasses();
      this._bindEvents();

      /* In embedded mode, skip loading overlay and don't auto-advance */
      if (this._isExpoMode) {
        /* Preload first 3 images only, then reveal */
        const preloadCount = Math.min(3, SLIDES.length);
        let loaded = 0;
        const onLoad = () => {
          loaded++;
          if (loaded >= preloadCount) {
            if (this._loading) {
              this._loading.classList.add('fade-out');
              setTimeout(() => {
                if (this._loading) this._loading.style.display = 'none';
              }, 700);
            }
            this._startAutoAdvance();
          }
        };
        for (let i = 0; i < preloadCount; i++) {
          const img = new Image();
          img.onload  = onLoad;
          img.onerror = onLoad;
          img.src     = this._sizedBg(SLIDES[i].bg);
        }
      }
      /* Embedded mode: no loading overlay, no auto-advance on init */

      /* No bulk preload: _applyBgWindow keeps only the slides near the
         current index loaded. Eagerly decoding all 47 full-bleed images
         is what crashed mobile tabs. */
    }

    /* ── FULLSCREEN BUTTON ──────────────────────────────────── */

    _buildFullscreenButton () {
      const btn = document.createElement('button');
      btn.className   = 'lkbk-fullscreen-btn';
      btn.type        = 'button';
      btn.setAttribute('aria-label', 'View lookbook fullscreen');
      btn.innerHTML   = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" stroke-width="2.5" stroke-linecap="round"
          stroke-linejoin="round" aria-hidden="true">
          <polyline points="15 3 21 3 21 9"></polyline>
          <polyline points="9 21 3 21 3 15"></polyline>
          <line x1="21" y1="3" x2="14" y2="10"></line>
          <line x1="3" y1="21" x2="10" y2="14"></line>
        </svg>
        Fullscreen`;
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const url = new URL(window.location.href);
        url.searchParams.set('display', 'expo');
        window.location.href = url.toString();
      });
      this.appendChild(btn);
      this._fullscreenBtn = btn;
    }

    /* ── CLOSE BUTTON (fullscreen mode) ────────────────────── */

    _buildCloseButton () {
      const btn = document.createElement('button');
      btn.className   = 'lkbk-close-btn';
      btn.type        = 'button';
      btn.setAttribute('aria-label', 'Exit fullscreen');
      btn.innerHTML   = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" stroke-width="2.5" stroke-linecap="round"
          stroke-linejoin="round" aria-hidden="true">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>`;
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        this._exitFullscreen();
      });
      this.appendChild(btn);
      this._closeBtn = btn;
    }

    /* ── FULLSCREEN ENTER / EXIT ────────────────────────────── */

    _applyFullscreenMode () {
      this.classList.remove('is-embedded');
      this.classList.add('is-fullscreen');
      document.body.dataset.lkbkMode = 'expo';
      document.body.style.overflow   = 'hidden';

      /* Request Fullscreen API (requires user gesture; call via button or auto) */
      const requestFs = () => {
        if (this.requestFullscreen) {
          this.requestFullscreen().catch(() => {/* silently fail */});
        } else if (this.webkitRequestFullscreen) {
          this.webkitRequestFullscreen();
        }
      };
      /* Attempt on user first interaction (since it requires gesture) */
      const onGesture = () => {
        requestFs();
        document.removeEventListener('click', onGesture, true);
        document.removeEventListener('touchend', onGesture, true);
      };
      document.addEventListener('click', onGesture, { once: true, capture: true });
      document.addEventListener('touchend', onGesture, { once: true, capture: true });

      /* Wake-lock: keep screen on during expo */
      if ('wakeLock' in navigator) {
        navigator.wakeLock.request('screen').catch(() => {/* HTTPS-only, ignore on dev */});
      }

      /* Mark hosting section */
      const section = this.closest('.shopify-section');
      if (section) section.classList.add('lkbk-hosting-section');

      /* Idle reset: after 30s of inactivity, return to slide 0 */
      this._bindIdleReset();
    }

    _enterFullscreen () {
      this._stopAutoAdvance();
      this._applyFullscreenMode();
      this._startAutoAdvance();
    }

    _exitFullscreen () {
      this.classList.remove('is-fullscreen');
      this.classList.add('is-embedded');
      delete document.body.dataset.lkbkMode;
      document.body.style.overflow = '';
      this._stopAutoAdvance();
      this._isExpoMode = false;

      /* Strip ?display=expo from the URL so reloading or sharing the
         link doesn't snap the page back into fullscreen mode. */
      try {
        const url = new URL(window.location.href);
        if (url.searchParams.get('display') === 'expo') {
          url.searchParams.delete('display');
          const newUrl = url.pathname + (url.search ? url.search : '') + url.hash;
          window.history.replaceState({}, '', newUrl);
        }
      } catch (e) { /* ignore */ }

      /* Release wake-lock */
      if (this._wakeLock) {
        this._wakeLock.release().catch(() => {});
        this._wakeLock = null;
      }

      /* Exit browser fullscreen if active */
      if (document.fullscreenElement || document.webkitFullscreenElement) {
        if (document.exitFullscreen) document.exitFullscreen().catch(() => {});
        else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
      }

      /* Strip ?display=expo so a reload (esp. on iPad) doesn't re-trigger fullscreen */
      const url = new URL(window.location.href);
      if (url.searchParams.has('display')) {
        url.searchParams.delete('display');
        const search = url.searchParams.toString();
        history.replaceState(null, '', url.pathname + (search ? '?' + search : '') + url.hash);
      }
    }

    /* ── IDLE RESET (expo / fullscreen mode only) ─────────────── */

    _bindIdleReset () {
      const IDLE_MS = 30000;
      const resetIdleTimer = () => {
        clearTimeout(this._expoIdleTimer);
        this._expoIdleTimer = setTimeout(() => {
          /* After 30s idle in expo mode, snap back to slide 0 */
          if (this.classList.contains('is-fullscreen')) {
            this._goTo(0);
            this._isPaused = false;
            this._startAutoAdvance();
          }
        }, IDLE_MS);
      };
      document.addEventListener('touchstart', resetIdleTimer, { passive: true });
      document.addEventListener('click', resetIdleTimer);
      document.addEventListener('mousemove', resetIdleTimer);
      resetIdleTimer();
    }

    disconnectedCallback () {
      this._stopAutoAdvance();
    }

    /* ── SLIDE BUILDING ─────────────────────────────────────── */

    /* Request a viewport-appropriate render from the Shopify CDN instead
       of the multi-MB original. Mobile tabs cannot hold 47 full-res
       bitmaps in memory at once. */
    _sizedBg (url) {
      if (!url || url.indexOf('cdn.shopify.com') === -1) return url;
      if (/[?&]width=/.test(url)) return url;
      return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=1600';
    }

    _applyBgWindow (center) {
      if (!this._bgEls) return;
      this._bgEls.forEach((bg, i) => {
        const inWindow = Math.abs(i - center) <= this._bgWindowRadius;
        const url = bg.dataset.bgUrl;
        if (inWindow && url) {
          if (!bg.style.backgroundImage) {
            bg.style.backgroundImage = 'url(' + url + ')';
          }
        } else if (bg.style.backgroundImage) {
          bg.style.backgroundImage = '';
        }
      });
    }

    _buildSlides () {
      this._bgEls = [];
      SLIDES.forEach((data, i) => {
        const slide = document.createElement('div');
        slide.className = 'lkbk-slide' + (i === 0 ? ' active' : '');
        if (data.type === 'hook')    slide.classList.add('lkbk-slide--hook');
        if (data.type === 'cta')     slide.classList.add('lkbk-slide--cta');
        if (data.type === 'chapter') slide.classList.add('lkbk-slide--chapter');
        slide.setAttribute('role', 'group');
        slide.setAttribute('aria-label', 'Slide ' + (i + 1) + ' of ' + this._totalSlides);
        slide.dataset.index = i;

        /* Background
           Bug B fix: append a comment node so this div is not matched by
           the theme-wide `div:empty { display: none }` rule in base.css */
        const bg = document.createElement('div');
        bg.className = 'lkbk-slide__bg';
        /* Background image is applied lazily by _applyBgWindow so only a
           few decoded bitmaps are resident at once (mobile memory cap). */
        bg.dataset.bgUrl = this._sizedBg(data.bg);
        bg.style.backgroundPosition = data.bgPosition || 'center center';
        bg.appendChild(document.createComment(' image '));
        slide.appendChild(bg);
        this._bgEls.push(bg);

        /* Overlay — same empty-div workaround */
        const overlay = document.createElement('div');
        overlay.className = 'lkbk-slide__overlay';
        overlay.appendChild(document.createComment(' overlay '));
        slide.appendChild(overlay);

        /* Content */
        slide.appendChild(this._buildContent(data));

        this._carousel.appendChild(slide);
      });
    }

    _buildContent (data) {
      const c = document.createElement('div');
      c.className = 'lkbk-slide__content';

      if (data.type === 'hook') {
        /* Pride bar */
        const prideBar = document.createElement('div');
        prideBar.className = 'lkbk-pride-bar';
        prideBar.setAttribute('aria-hidden', 'true');
        ['#FF0018','#FFA52C','#FFFF41','#008018','#0000F9','#86007D'].forEach(col => {
          const s = document.createElement('span');
          s.style.background = col;
          prideBar.appendChild(s);
        });
        c.appendChild(prideBar);

        const wordmark = document.createElement('div');
        wordmark.className   = 'lkbk-brand-wordmark';
        wordmark.textContent = 'Midnight Muse Made';
        c.appendChild(wordmark);

        if (data.eyebrow) {
          const ey = document.createElement('span');
          ey.className   = 'lkbk-eyebrow';
          ey.textContent = data.eyebrow;
          c.appendChild(ey);
        }

        const h = document.createElement('h1');
        h.className = 'lkbk-heading';
        h.innerHTML = data.heading;
        c.appendChild(h);

        if (data.body) {
          const p = document.createElement('p');
          p.className   = 'lkbk-body';
          p.textContent = data.body;
          c.appendChild(p);
        }
        return c;
      }

      if (data.type === 'chapter') {
        const num = document.createElement('div');
        num.className   = 'lkbk-chapter-number';
        num.textContent = data.chapterNum;
        c.appendChild(num);

        const orn = document.createElement('div');
        orn.className = 'lkbk-chapter-ornament';
        orn.setAttribute('aria-hidden', 'true');
        orn.textContent = '✦';
        c.appendChild(orn);

        const h = document.createElement('h2');
        h.className = 'lkbk-heading';
        h.innerHTML = data.heading;
        c.appendChild(h);

        if (data.tagline) {
          const tl = document.createElement('p');
          tl.className   = 'lkbk-chapter-tagline';
          tl.textContent = data.tagline;
          c.appendChild(tl);
        }
        return c;
      }

      if (data.type === 'cta') {
        const h = document.createElement('h2');
        h.className = 'lkbk-heading';
        h.innerHTML = data.heading;
        c.appendChild(h);

        if (data.body) {
          const p = document.createElement('p');
          p.className = 'lkbk-body';
          p.innerHTML = data.body;
          c.appendChild(p);
        }

        /* Cards row */
        const cards = document.createElement('div');
        cards.className = 'lkbk-cta-cards';

        /* Email form */
        const form = document.createElement('div');
        form.className = 'lkbk-form-card';
        form.innerHTML = `
          <button class="lkbk-form-btn" type="button"
            aria-label="Close fullscreen">Sign up for more info</button>`;

        const btn = form.querySelector('.lkbk-form-btn');
        btn.addEventListener('click', () => {
          this._exitFullscreen();
        });

        cards.appendChild(form);
        c.appendChild(cards);

        /* Restart button — visible only on last slide via CSS .is-last rule */
        const restartBtn = this._restart;
        if (restartBtn) {
          c.appendChild(restartBtn.cloneNode(true));
          /* Wire the cloned restart; the original in DOM is hidden */
        }

        return c;
      }

      /* Default: product slide */
      if (data.eyebrow) {
        const ey = document.createElement('span');
        ey.className   = 'lkbk-eyebrow';
        ey.textContent = data.eyebrow;
        c.appendChild(ey);
      }

      const h = document.createElement('h2');
      h.className = 'lkbk-heading';
      h.innerHTML = data.heading;
      c.appendChild(h);

      if (data.body) {
        const p = document.createElement('p');
        p.className = 'lkbk-body';
        /* Use innerHTML so <em> highlights from blurb fields render correctly */
        p.innerHTML = data.body;
        c.appendChild(p);
      }

      if (data.product) {
        const pn = document.createElement('span');
        pn.className   = 'lkbk-product-name';
        pn.textContent = data.product;
        c.appendChild(pn);
      }

      return c;
    }

    /* ── PROGRESS BARS ────────────────────────────────────────── */

    _buildProgressBars () {
      this._strip.innerHTML = '';
      for (let i = 0; i < this._totalSlides; i++) {
        const seg  = document.createElement('div');
        seg.className    = 'lkbk-progress-seg';
        seg.dataset.index = i;
        seg.setAttribute('role', 'button');
        seg.tabIndex = 0;
        seg.setAttribute('aria-label', `Go to slide ${i + 1}`);
        const fill = document.createElement('div');
        fill.className   = 'lkbk-fill';
        seg.appendChild(fill);

        seg.addEventListener('click', (e) => {
          e.stopPropagation();
          this._goTo(i);
        });
        seg.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            this._goTo(i);
          }
        });

        this._strip.appendChild(seg);
      }
    }

    _updateProgressBars (index) {
      const segs = this._strip.querySelectorAll('.lkbk-progress-seg');
      segs.forEach((seg, i) => {
        seg.classList.remove('done', 'active');
        const fill = seg.querySelector('.lkbk-fill');
        fill.style.animation = 'none';
        void fill.offsetWidth; /* force reflow to restart animation */
        if (i < index) {
          seg.classList.add('done');
        } else if (i === index) {
          seg.classList.add('active');
          fill.style.animation = '';
        }
      });
    }

    /* ── NAVIGATION ───────────────────────────────────────────── */

    _goTo (index) {
      if (index < 0 || index >= this._totalSlides) return;

      const slides = this._carousel.querySelectorAll('.lkbk-slide');
      slides[this._currentIndex].classList.remove('active');
      this._currentIndex = index;
      slides[this._currentIndex].classList.add('active');

      this._applyBgWindow(this._currentIndex);

      this._carousel.style.transform = 'translateX(-' + (this._currentIndex * 100) + '%)';

      this._updateProgressBars(this._currentIndex);
      this._updateChapterLabel();
      this._updateCounter();
      this._updateRootClasses();
      this._resetAutoAdvance();
    }

    _goNext () {
      if (this._currentIndex < this._totalSlides - 1) {
        this._goTo(this._currentIndex + 1);
      } else {
        /* Swipe forward from last slide wraps to first */
        this._goTo(0);
      }
    }

    _goPrev () {
      this._goTo(Math.max(this._currentIndex - 1, 0));
    }

    _updateChapterLabel () {
      const chapter = SLIDES[this._currentIndex].chapter;
      if (chapter && CHAPTERS[chapter]) {
        this._chapter.textContent  = CHAPTERS[chapter];
        this._chapter.style.opacity = '1';
      } else {
        this._chapter.style.opacity = '0';
      }
    }

    _updateCounter () {
      this._counter.textContent = (this._currentIndex + 1) + ' / ' + this._totalSlides;
    }

    _updateRootClasses () {
      this._root.classList.toggle('is-first', this._currentIndex === 0);
      this._root.classList.toggle('is-last',  this._currentIndex === this._totalSlides - 1);
    }

    /* ── AUTO-ADVANCE (with loop) ─────────────────────────────── */

    _startAutoAdvance () {
      this._stopAutoAdvance();
      if (this._isPaused) return;
      /* Auto-advance only runs in fullscreen/expo mode */
      if (!this.classList.contains('is-fullscreen')) return;

      /* Auto-advance loops back to slide 0 after the last (CTA) slide */
      this._autoTimer = setTimeout(() => {
        if (this._currentIndex >= this._totalSlides - 1) {
          this._goTo(0); /* loop */
        } else {
          this._goNext();
        }
      }, 6000);
    }

    _stopAutoAdvance () {
      if (this._autoTimer) {
        clearTimeout(this._autoTimer);
        this._autoTimer = null;
      }
    }

    _resetAutoAdvance () {
      this._stopAutoAdvance();
      this._startAutoAdvance();
    }

    /* ── EVENT BINDING ────────────────────────────────────────── */

    _bindEvents () {
      /* Touch swipe */
      this._carousel.addEventListener('touchstart', (e) => {
        this._touchStartX = e.touches[0].clientX;
        this._touchStartY = e.touches[0].clientY;
        this._dragOffset  = 0;
        this._isDragging  = false;
        this._stopAutoAdvance();
        /* Hold-to-pause */
        this._holdTimer = setTimeout(() => {
          this._isPaused = true;
          this._stopAutoAdvance();
        }, 200);
      }, { passive: true });

      this._carousel.addEventListener('touchmove', (e) => {
        const dx = e.touches[0].clientX - this._touchStartX;
        const dy = e.touches[0].clientY - this._touchStartY;
        if (!this._isDragging && Math.abs(dy) > Math.abs(dx)) return;
        this._isDragging = true;
        this._dragOffset = dx;
        const base = -(this._currentIndex * 100);
        this._carousel.classList.add('dragging');
        this._carousel.style.transform =
          'translateX(calc(' + base + '% + ' + this._dragOffset + 'px))';
      }, { passive: true });

      this._carousel.addEventListener('touchend', () => {
        clearTimeout(this._holdTimer);
        this._carousel.classList.remove('dragging');
        if (this._isDragging && Math.abs(this._dragOffset) > 50) {
          if (this._dragOffset < 0) this._goNext();
          else                      this._goPrev();
        } else {
          this._carousel.style.transform =
            'translateX(-' + (this._currentIndex * 100) + '%)';
          this._isPaused = false;
          this._startAutoAdvance();
        }
        this._isDragging = false;
        this._dragOffset = 0;
      });

      /* Mouse drag (desktop testing) */
      this._carousel.addEventListener('mousedown', (e) => {
        this._mouseDown   = true;
        this._mouseStartX = e.clientX;
        this._dragOffset  = 0;
        this._stopAutoAdvance();
      });

      window.addEventListener('mousemove', (e) => {
        if (!this._mouseDown) return;
        this._dragOffset = e.clientX - this._mouseStartX;
        const base = -(this._currentIndex * 100);
        this._carousel.classList.add('dragging');
        this._carousel.style.transform =
          'translateX(calc(' + base + '% + ' + this._dragOffset + 'px))';
      });

      window.addEventListener('mouseup', () => {
        if (!this._mouseDown) return;
        this._mouseDown = false;
        this._carousel.classList.remove('dragging');
        if (Math.abs(this._dragOffset) > 50) {
          if (this._dragOffset < 0) this._goNext();
          else                      this._goPrev();
        } else {
          this._carousel.style.transform =
            'translateX(-' + (this._currentIndex * 100) + '%)';
          this._startAutoAdvance();
        }
      });

      /* Nav tap zones */
      this._navPrev.addEventListener('click', (e) => {
        e.stopPropagation();
        this._goPrev();
      });
      this._navNext.addEventListener('click', (e) => {
        e.stopPropagation();
        this._goNext();
      });

      this._navPrev.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this._goPrev(); }
      });
      this._navNext.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this._goNext(); }
      });

      /* Keyboard navigation */
      document.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft')  this._goPrev();
        if (e.key === 'ArrowRight') this._goNext();
        if (e.key === ' ') {
          e.preventDefault();
          this._isPaused = !this._isPaused;
          this._isPaused ? this._stopAutoAdvance() : this._startAutoAdvance();
        }
        /* Esc exits fullscreen mode */
        if (e.key === 'Escape' && this.classList.contains('is-fullscreen')) {
          this._exitFullscreen();
        }
      });

      /* Tap anywhere in fullscreen briefly reveals the close button */
      this.addEventListener('click', (e) => {
        if (!this.classList.contains('is-fullscreen')) return;
        if (e.target.closest('.lkbk-close-btn')) return;
        if (e.target.closest('.lkbk-restart-btn')) return;
        if (e.target.closest('.lkbk-help-btn')) return;
        if (!this._closeBtn) return;
        this._closeBtn.classList.add('close-visible');
        clearTimeout(this._closeBtnTimer);
        this._closeBtnTimer = setTimeout(() => {
          if (this._closeBtn) this._closeBtn.classList.remove('close-visible');
        }, 3000);
      });

      /* Restart button — wired via event delegation on the root */
      this._root.addEventListener('click', (e) => {
        if (e.target.closest('.lkbk-restart-btn')) {
          this._goTo(0);
        }
      });

      /* Help tooltip */
      if (this._helpBtn && this._helpTip) {
        this._helpBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          this._helpVisible = !this._helpVisible;
          this._helpTip.classList.toggle('visible', this._helpVisible);
          this._helpBtn.setAttribute('aria-expanded', String(this._helpVisible));
          this._helpTip.setAttribute('aria-hidden', String(!this._helpVisible));

          if (this._helpVisible) {
            this._stopAutoAdvance();
            clearTimeout(this._helpDismiss);
            this._helpDismiss = setTimeout(() => {
              this._helpVisible = false;
              this._helpTip.classList.remove('visible');
              this._helpBtn.setAttribute('aria-expanded', 'false');
              this._helpTip.setAttribute('aria-hidden', 'true');
              this._startAutoAdvance();
            }, 5000);
          } else {
            clearTimeout(this._helpDismiss);
            this._startAutoAdvance();
          }
        });

        document.addEventListener('click', (e) => {
          if (this._helpVisible &&
              !this._helpTip.contains(e.target) &&
              e.target !== this._helpBtn) {
            this._helpVisible = false;
            this._helpTip.classList.remove('visible');
            this._helpBtn.setAttribute('aria-expanded', 'false');
            this._helpTip.setAttribute('aria-hidden', 'true');
            clearTimeout(this._helpDismiss);
            this._startAutoAdvance();
          }
        });
      }

      /* Idle detection — show chevrons after 2s without interaction */
      const resetIdle = () => {
        if (this._inner) {
          this._inner.classList.remove('nav-visible');
          clearTimeout(this._idleTimer);
          this._idleTimer = setTimeout(() => {
            if (this._inner) this._inner.classList.add('nav-visible');
          }, 2000);
        }
      };
      document.addEventListener('touchstart', resetIdle, { passive: true });
      document.addEventListener('mousemove', resetIdle);
    }
  }

  /* Register only once (HMR / theme editor re-renders guard) */
  if (!customElements.get('lookbook-carousel')) {
    customElements.define('lookbook-carousel', LookbookCarousel);
  }

}());
